import type { FormEvent } from "react";
import { useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../../api";
import type { PageSocialLink } from "../../api/pages";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { SectionHeader } from "../../components/ui/section-header";
import { ModalShell } from "../../components/ui/modal";
import { useLanguage } from "../../hooks/useLanguage";

type SocialLinksProps = {
  pageId: number | null;
  initialLinks?: PageSocialLink[];
  onUpdate?: (links: PageSocialLink[]) => void;
};

type SocialLinkFormState = {
  id?: number;
  network: string;
  username: string;
};

type ModalState = {
  open: boolean;
  saving: boolean;
  error: string | null;
  form: SocialLinkFormState;
};

function createEmptyForm(): SocialLinkFormState {
  return {
    network: "instagram",
    username: "",
  };
}

export function SocialLinks({ pageId, initialLinks = [], onUpdate }: SocialLinksProps) {
  const { t } = useLanguage();

  const SOCIAL_NETWORKS = [
    { type: "instagram", label: t.pageBuilder.socialLinks.networks.instagram.label, placeholder: t.pageBuilder.socialLinks.networks.instagram.placeholder },
    { type: "facebook", label: t.pageBuilder.socialLinks.networks.facebook.label, placeholder: t.pageBuilder.socialLinks.networks.facebook.placeholder },
    { type: "tiktok", label: t.pageBuilder.socialLinks.networks.tiktok.label, placeholder: t.pageBuilder.socialLinks.networks.tiktok.placeholder },
    { type: "youtube", label: t.pageBuilder.socialLinks.networks.youtube.label, placeholder: t.pageBuilder.socialLinks.networks.youtube.placeholder },
    { type: "twitter", label: t.pageBuilder.socialLinks.networks.twitter.label, placeholder: t.pageBuilder.socialLinks.networks.twitter.placeholder },
    { type: "linkedin", label: t.pageBuilder.socialLinks.networks.linkedin.label, placeholder: t.pageBuilder.socialLinks.networks.linkedin.placeholder },
  ];

  const [links, setLinks] = useState<PageSocialLink[]>(initialLinks);
  const [modal, setModal] = useState<ModalState>({
    open: false,
    saving: false,
    error: null,
    form: createEmptyForm(),
  });

  const updateLinks = (newLinks: PageSocialLink[]) => {
    setLinks(newLinks);
    onUpdate?.(newLinks);
  };

  const ensurePage = () => {
    if (!pageId) {
      window.alert(t.pageBuilder.socialLinks.savePageFirst);
      return false;
    }
    return true;
  };

  const openCreate = () => {
    if (!ensurePage()) return;
    setModal({
      open: true,
      saving: false,
      error: null,
      form: createEmptyForm(),
    });
  };

  const openEdit = (link: PageSocialLink) => {
    if (!ensurePage()) return;
    setModal({
      open: true,
      saving: false,
      error: null,
      form: {
        id: link.id,
        network: link.network,
        username: link.username,
      },
    });
  };

  const closeModal = () => {
    setModal({
      open: false,
      saving: false,
      error: null,
      form: createEmptyForm(),
    });
  };

  const setField =
    (field: keyof SocialLinkFormState) => (value: string) => {
      setModal(prev => ({
        ...prev,
        form: {
          ...prev.form,
          [field]: value,
        },
      }));
    };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!pageId) {
      window.alert(t.pageBuilder.socialLinks.savePageFirst);
      return;
    }

    setModal(prev => ({ ...prev, saving: true, error: null }));

    const { id, network, username } = modal.form;
    const payload = {
      pageId,
      network,
      username,
      id,
    };

    const res = id
      ? await api.updatePageSocialLink(payload)
      : await api.createPageSocialLink(payload);

    if (!res.ok) {
      setModal(prev => ({
        ...prev,
        saving: false,
        error: res.error,
      }));
      return;
    }

    const saved = res.data as PageSocialLink;

    let newLinks;
    if (id) {
      newLinks = links.map(l => (l.id === saved.id ? saved : l));
    } else {
      newLinks = [...links, saved];
    }
    updateLinks(newLinks);

    setModal(prev => ({ ...prev, saving: false }));
    closeModal();
  };

  const handleDelete = async (link: PageSocialLink) => {
    if (!pageId) {
      window.alert(t.pageBuilder.socialLinks.savePageFirst);
      return;
    }
    if (!window.confirm(t.pageBuilder.socialLinks.confirmDelete)) {
      return;
    }

    const res = await api.deletePageSocialLink(link.id);
    if (!res.ok) {
      window.alert(res.error || t.pageBuilder.socialLinks.deleteError);
      return;
    }

    const newLinks = links.filter(l => l.id !== link.id);
    updateLinks(newLinks);
  };

  const currentNetworkDef = (network: string) =>
    SOCIAL_NETWORKS.find(n => n.type === network) || SOCIAL_NETWORKS[0];

  return (
    <>
      <Card className="space-y-4">
        <SectionHeader
          title={t.pageBuilder.socialLinks.title}
          action={
            <Button size="micro" onClick={openCreate} disabled={!pageId}>
              {t.pageBuilder.socialLinks.add}
            </Button>
          }
        />

        {links.length === 0 ? (
          <p className="text-xs text-neutral-500">
            {t.pageBuilder.socialLinks.empty}
          </p>
        ) : (
          <div className="space-y-2">
            {links.map(link => {
              const def = currentNetworkDef(link.network);
              return (
                <div
                  key={link.id}
                  className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 rounded-lg border border-neutral-200 px-3 py-2 text-sm"
                >
                  <div>
                    <p className="text-sm font-semibold text-neutral-800">
                      {def?.label || link.network}
                      <span className="ml-1 text-xs text-neutral-500">@{link.username}</span>
                    </p>
                    <p className="text-[11px] text-neutral-500 break-all mt-0.5">{link.url}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="micro"
                      variant="ghost"
                      onClick={() => openEdit(link)}
                    >
                      {t.pageBuilder.socialLinks.edit}
                    </Button>
                    <Button
                      type="button"
                      size="micro"
                      variant="destructive"
                      onClick={() => handleDelete(link)}
                    >
                      {t.pageBuilder.socialLinks.remove}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {modal.open && typeof document !== 'undefined' && createPortal(
        <ModalShell open={modal.open} onClose={closeModal} disableBackdropClose={modal.saving}>
          <div className="relative w-full rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-xl font-semibold mb-4">
              {modal.form.id ? t.pageBuilder.socialLinks.modal.editTitle : t.pageBuilder.socialLinks.modal.addTitle}
            </h2>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <Label htmlFor="social_network">{t.pageBuilder.socialLinks.modal.network}</Label>
                <select
                  id="social_network"
                  value={modal.form.network}
                  onChange={e => setField("network")(e.target.value)}
                  className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm"
                >
                  {SOCIAL_NETWORKS.map(network => (
                    <option key={network.type} value={network.type}>
                      {network.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="social_username">{t.pageBuilder.socialLinks.modal.username}</Label>
                <Input
                  id="social_username"
                  value={modal.form.username}
                  onChange={e => setField("username")(e.target.value)}
                  placeholder={currentNetworkDef(modal.form.network)?.placeholder || "@yourusername"}
                  required
                />
              </div>

              {modal.error && <p className="text-sm text-red-600">{modal.error}</p>}

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="ghost" onClick={closeModal}>
                  {t.pageBuilder.socialLinks.modal.cancel}
                </Button>
                <Button type="submit" disabled={modal.saving}>
                  {modal.saving ? t.pageBuilder.socialLinks.modal.saving : modal.form.id ? t.pageBuilder.socialLinks.modal.save : t.pageBuilder.socialLinks.modal.add}
                </Button>
              </div>
            </form>
          </div>
        </ModalShell>,
        document.body
      )}
    </>
  );
}
